/**
 * FIELD-001C Section B · Attributed notes + photographic evidence.
 *
 * Renders the task's evidence history as an append-only chronological
 * list (author + timestamp, immutable body + photos) plus an
 * "Add note" composer with photo picker. Reads/writes are gated
 * server-side; this component defends the UX by hiding the composer
 * when the caller cannot contribute and by keeping post retries
 * idempotent.
 *
 * Props:
 *   • taskRef       — canonical ref string ("adhoc:{id}" or
 *                     "canonical:{project_id}:{task_id}").
 *   • legacyNote    — the historical single-blob `notes` value
 *                     rendered read-only as "Legacy note". Never
 *                     duplicated on refetch, never given a synthetic
 *                     author/date.
 *   • canContribute — server contract mirror. Composer is hidden
 *                     when false.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, API, formatApiError, getToken } from "@/lib/api";
import { toast } from "sonner";
import {
    Paperclip, Send, X, Image as ImageIcon, Loader2, Download, AlertCircle,
} from "lucide-react";

const MAX_PHOTO_MB = 10;
const MAX_PHOTO_COUNT = 10;
// Aggregate cap must match the server so a request rejected by the
// backend cannot pass the composer. Sits under Mongo's 16 MB BSON
// document ceiling once base64 (~4/3) inflation is applied.
const MAX_TOTAL_MB = 10;
const ACCEPTED_MIME = new Set(["image/jpeg", "image/png", "image/webp"]);

function formatWhen(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, {
            month: "short", day: "numeric", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
}

// Deterministic idempotency key for a single composer session. Once
// the client stages a submission, retries reuse the same key so the
// server can dedupe if the network stutters after the row has
// already been persisted.
function _uuid() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function EntryPhoto({ entryId, photo }) {
    const [thumbSrc, setThumbSrc] = useState(null);
    const [tokenErr, setTokenErr] = useState(false);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { token } = await api.taskEntryPhotoToken(entryId, photo.id);
                if (cancelled) return;
                setThumbSrc(`${API}/task-entries/${entryId}/photos/${photo.id}?dt=${encodeURIComponent(token)}`);
            } catch {
                if (!cancelled) setTokenErr(true);
            }
        })();
        return () => { cancelled = true; };
    }, [entryId, photo.id]);
    const [viewer, setViewer] = useState(false);
    const openViewer = () => setViewer(true);
    return (
        <>
            <button
                type="button"
                onClick={openViewer}
                disabled={!thumbSrc}
                data-testid={`task-entry-photo-${photo.id}`}
                aria-label={`Open photo ${photo.file_name || photo.id}`}
                className="w-20 h-20 rounded-md overflow-hidden border border-[#E5E7EB] bg-[#F5F5F7] flex items-center justify-center disabled:cursor-wait"
            >
                {thumbSrc ? (
                    <img src={thumbSrc} alt={photo.file_name || "Evidence photo"}
                         className="w-full h-full object-cover" />
                ) : tokenErr ? (
                    <AlertCircle size={16} className="text-[#DA3535]" />
                ) : (
                    <Loader2 size={16} className="text-[#4B5565] animate-spin" />
                )}
            </button>
            {viewer && (
                <div
                    className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4"
                    onClick={() => setViewer(false)}
                    data-testid={`task-entry-photo-viewer-${photo.id}`}
                >
                    <div className="max-w-[92vw] max-h-[92vh] flex flex-col items-center gap-3"
                         onClick={(e) => e.stopPropagation()}>
                        <img
                            src={thumbSrc}
                            alt={photo.file_name || "Evidence photo"}
                            className="max-w-full max-h-[80vh] object-contain rounded"
                        />
                        <div className="flex items-center gap-2 text-white/85 text-xs">
                            <span className="truncate max-w-[60vw]">{photo.file_name || "Evidence"}</span>
                            <a
                                href={thumbSrc ? `${thumbSrc}&download=1` : "#"}
                                data-testid={`task-entry-photo-download-${photo.id}`}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white/10 hover:bg-white/20"
                            >
                                <Download size={12} /> Download
                            </a>
                            <button
                                type="button"
                                onClick={() => setViewer(false)}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white/10 hover:bg-white/20"
                            >
                                <X size={12} /> Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default function TaskEntries({ taskRef, legacyNote = "", canContribute = false }) {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");

    const reload = useCallback(async () => {
        if (!taskRef) return;
        setLoading(true);
        setLoadError("");
        try {
            const { entries: rows } = await api.listTaskEntries(taskRef);
            setEntries(rows || []);
        } catch (e) {
            setLoadError(formatApiError(e?.response?.data?.detail) || "Could not load evidence.");
        } finally { setLoading(false); }
    }, [taskRef]);

    useEffect(() => { reload(); }, [reload]);

    // Composer state — draft body + queued photos + retryable
    // submission. Idempotency key is generated once per staged
    // submission and reused across retries. Cleared on success.
    const [body, setBody] = useState("");
    const [staged, setStaged] = useState([]); // [{id, file, previewUrl, sizeMb}]
    const [posting, setPosting] = useState(false);
    const [error, setError] = useState("");
    const idemRef = useRef(null);
    const fileInputRef = useRef(null);

    useEffect(() => () => {
        // Free preview blob URLs when the component unmounts.
        staged.forEach((s) => { try { URL.revokeObjectURL(s.previewUrl); } catch {} });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function addFiles(list) {
        // Additive validation: each incoming file is evaluated
        // against the per-file limit AND the running aggregate of
        // already-staged photos plus everything accepted earlier in
        // this batch. Files that don't fit are skipped with an
        // actionable error; the composer body, prior valid photos
        // and any earlier-accepted files in the same batch are
        // preserved so the operator can drop the offending photo
        // and retry.
        const softErrors = [];
        const accepted = [];
        let running = staged.reduce((n, s) => n + (s.file?.size || 0), 0);
        const totalCap = MAX_TOTAL_MB * 1024 * 1024;
        const perFileCap = MAX_PHOTO_MB * 1024 * 1024;
        for (const f of list) {
            if (staged.length + accepted.length >= MAX_PHOTO_COUNT) {
                softErrors.push(`"${f.name}" not added — the ${MAX_PHOTO_COUNT}-photos-per-note limit was reached.`);
                continue;
            }
            const mime = (f.type || "").toLowerCase();
            if (!ACCEPTED_MIME.has(mime)) {
                softErrors.push(`"${f.name}" isn't a supported image — JPEG, PNG or WebP only.`);
                continue;
            }
            if (f.size > perFileCap) {
                softErrors.push(`"${f.name}" exceeds the ${MAX_PHOTO_MB} MB per-photo limit.`);
                continue;
            }
            if (running + f.size > totalCap) {
                softErrors.push(`"${f.name}" would push this note over the ${MAX_TOTAL_MB} MB total limit. Remove another photo and try again.`);
                continue;
            }
            running += f.size;
            accepted.push({
                id: _uuid(),
                file: f,
                previewUrl: URL.createObjectURL(f),
                sizeMb: (f.size / (1024 * 1024)).toFixed(1),
            });
        }
        if (accepted.length) {
            setStaged((prev) => [...prev, ...accepted]);
        }
        // Show the actionable error(s) if any offending files were
        // dropped; clear otherwise. Note text and existing valid
        // photos are never touched by validation.
        if (softErrors.length) setError(softErrors.join(" "));
        else if (accepted.length) setError("");
    }

    function removeStaged(id) {
        setStaged((prev) => {
            const found = prev.find((s) => s.id === id);
            if (found) { try { URL.revokeObjectURL(found.previewUrl); } catch {} }
            return prev.filter((s) => s.id !== id);
        });
    }

    async function submit() {
        if (posting) return;
        const trimmed = body.trim();
        if (!trimmed && staged.length === 0) {
            setError("Add a note, one or more photos, or both.");
            return;
        }
        if (!idemRef.current) idemRef.current = _uuid();
        setPosting(true);
        setError("");
        try {
            const saved = await api.createTaskEntry({
                taskRef,
                body: trimmed,
                photos: staged.map((s) => s.file),
                idempotencyKey: idemRef.current,
            });
            // Success — append (never replace prior entries) and clear
            // the composer. Preview blobs freed. Idempotency key reset
            // so the next post gets a fresh key.
            setEntries((prev) => {
                if (prev.some((e) => e.id === saved.id)) return prev;
                return [...prev, saved];
            });
            staged.forEach((s) => { try { URL.revokeObjectURL(s.previewUrl); } catch {} });
            setStaged([]);
            setBody("");
            idemRef.current = null;
            toast.success("Note added");
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const msg = (typeof detail === "object" ? detail?.message : null)
                || formatApiError(detail)
                || "Could not add note. Try again.";
            setError(msg);
        } finally { setPosting(false); }
    }

    const legacyRow = useMemo(() => {
        const t = (legacyNote || "").trim();
        if (!t) return null;
        return (
            <div className="pe-card p-3 md:p-4 bg-[#F9F5EA] border border-[#E8DFBA]" data-testid="task-entries-legacy">
                <div className="text-[10px] uppercase tracking-[0.12em] text-[#8A6D1B] font-medium mb-1">Legacy note</div>
                <div className="text-sm text-[#131B2F] whitespace-pre-wrap break-words">{t}</div>
            </div>
        );
    }, [legacyNote]);

    return (
        <div className="space-y-3" data-testid="task-entries">
            <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-[#131B2F]">Notes &amp; evidence</label>
                <span className="text-[11px] text-[#4B5565]">
                    {loading ? "Loading…" : `${entries.length} ${entries.length === 1 ? "entry" : "entries"}`}
                </span>
            </div>

            {loadError && (
                <div className="text-sm text-[#DA3535]" data-testid="task-entries-load-error">{loadError}</div>
            )}

            <div className="space-y-3">
                {legacyRow}
                {entries.map((e) => (
                    <div
                        key={e.id}
                        data-testid={`task-entry-${e.id}`}
                        className="pe-card p-3 md:p-4 border border-[#E5E7EB]"
                    >
                        <div className="flex items-center justify-between gap-2 mb-1">
                            <div className="text-sm font-medium text-[#131B2F] truncate" data-testid={`task-entry-author-${e.id}`}>
                                {e.author_display || "Unknown"}
                            </div>
                            <div className="text-[11px] text-[#4B5565] shrink-0" data-testid={`task-entry-time-${e.id}`}>
                                {formatWhen(e.created_at)}
                            </div>
                        </div>
                        {e.body && (
                            <div className="text-sm text-[#131B2F] whitespace-pre-wrap break-words" data-testid={`task-entry-body-${e.id}`}>
                                {e.body}
                            </div>
                        )}
                        {(e.photos || []).length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2" data-testid={`task-entry-photos-${e.id}`}>
                                {e.photos.map((p) => (
                                    <EntryPhoto key={p.id} entryId={e.id} photo={p} />
                                ))}
                            </div>
                        )}
                    </div>
                ))}
                {!loading && !legacyRow && entries.length === 0 && (
                    <div className="text-xs text-[#4B5565]" data-testid="task-entries-empty">
                        No notes or photos yet.
                    </div>
                )}
            </div>

            {canContribute && (
                <div className="pe-card p-3 md:p-4 border border-dashed border-[#D1D5DB] space-y-2" data-testid="task-entries-composer">
                    <textarea
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        rows={2}
                        placeholder="Add a note. Photos optional."
                        maxLength={4000}
                        data-testid="task-entries-composer-body"
                        className="w-full px-3 py-2 border border-[#D1D5DB] rounded-md text-sm bg-white resize-y"
                    />
                    {staged.length > 0 && (
                        <div className="flex flex-wrap gap-2" data-testid="task-entries-composer-previews">
                            {staged.map((s) => (
                                <div key={s.id} className="relative w-20 h-20 rounded-md overflow-hidden border border-[#E5E7EB]">
                                    <img src={s.previewUrl} alt={s.file.name}
                                         className="w-full h-full object-cover" />
                                    <button
                                        type="button"
                                        onClick={() => removeStaged(s.id)}
                                        data-testid={`task-entries-composer-remove-${s.id}`}
                                        aria-label={`Remove ${s.file.name}`}
                                        className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/70 text-white flex items-center justify-center"
                                    >
                                        <X size={10} strokeWidth={2.5} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                multiple
                                capture="environment"
                                className="hidden"
                                data-testid="task-entries-composer-file-input"
                                onChange={(e) => {
                                    if (e.target.files) addFiles([...e.target.files]);
                                    // Reset so re-picking the same file re-fires change.
                                    e.target.value = "";
                                }}
                            />
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                data-testid="task-entries-composer-pick"
                                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-[#D1D5DB] bg-white text-xs font-medium text-[#131B2F] hover:bg-[#F5F5F7]"
                            >
                                <ImageIcon size={13} strokeWidth={1.75} />
                                {staged.length ? "Add more photos" : "Add photos"}
                            </button>
                            <span className="text-[10px] text-[#4B5565]" data-testid="task-entries-composer-hint-limits">
                                JPEG, PNG or WebP — up to {MAX_PHOTO_MB} MB per photo, {MAX_TOTAL_MB} MB total per note ({MAX_PHOTO_COUNT} max).
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={submit}
                            disabled={posting || (!body.trim() && staged.length === 0)}
                            data-testid="task-entries-composer-submit"
                            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-[#3E9E2E] text-white text-xs font-medium hover:bg-[#348426] disabled:opacity-50"
                        >
                            {posting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} strokeWidth={2} />}
                            {posting ? "Posting…" : "Post note"}
                        </button>
                    </div>
                    {error && <div className="text-xs text-[#DA3535]" data-testid="task-entries-composer-error">{error}</div>}
                    {!error && !posting && (
                        <div className="text-[10px] text-[#4B5565]" data-testid="task-entries-composer-hint">
                            Posting a note doesn't change status, description, assignment or completion.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// Silence noisy imports from bundlers that scan for `Paperclip` /
// `getToken` — kept only for future retry-with-token flows.
void Paperclip; void getToken;
